// ── Date helpers ─────────────────────────────────────────────────────────────
export const formatDate = (date, opts = {}) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', ...opts,
  });
};

export const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export const timeAgo = (date) => {
  if (!date) return '';
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800)return `${Math.floor(s / 86400)}d ago`;
  return formatDate(date);
};

export const daysUntil = (date) => {
  if (!date) return null;
  return Math.ceil((new Date(date) - new Date()) / 86400000);
};

export const isOverdue = (date) => date && new Date(date) < new Date();

// ── Currency helpers ──────────────────────────────────────────────────────────
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
};

export const formatCompactCurrency = (amount) => {
  if (amount == null) return '—';
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000)    return `$${(amount / 1000).toFixed(1)}K`;
  return `$${amount}`;
};

// ── String helpers ────────────────────────────────────────────────────────────
export const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

export const initials = (name) =>
  name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';

export const truncate = (str, len = 60) =>
  str && str.length > len ? str.slice(0, len) + '…' : (str || '');

// ── Number helpers ────────────────────────────────────────────────────────────
export const formatHours = (hours) => {
  if (hours == null) return '—';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

export const percentage = (part, total) =>
  total > 0 ? Math.round((part / total) * 100) : 0;

// ── Status helpers ─────────────────────────────────────────────────────────────
export const statusColor = {
  // Task statuses
  todo:        '#718096',
  'in-progress':'#4299e1',
  review:      '#ed8936',
  completed:   '#48bb78',
  blocked:     '#f56565',
  // Priority
  low:         '#a0aec0',
  medium:      '#ed8936',
  high:        '#f56565',
  urgent:      '#9b2335',
  // Leave statuses
  pending:     '#ed8936',
  approved:    '#48bb78',
  rejected:    '#f56565',
  cancelled:   '#a0aec0',
  // Attendance
  present:     '#48bb78',
  absent:      '#f56565',
  late:        '#ed8936',
  'half-day':  '#ecc94b',
  holiday:     '#667eea',
  weekend:     '#a0aec0',
};

export const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };

// ── Validation helpers ─────────────────────────────────────────────────────────
export const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const isValidPhone = (phone) => /^[6-9]\d{9}$/.test(phone.replace(/\D/g, ''));

// ── Local storage helpers ──────────────────────────────────────────────────────
export const storage = {
  get:    (key) => { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } },
  set:    (key, val) => localStorage.setItem(key, JSON.stringify(val)),
  remove: (key) => localStorage.removeItem(key),
};

// ── Array helpers ─────────────────────────────────────────────────────────────
export const groupBy = (arr, key) =>
  arr.reduce((acc, item) => {
    const k = typeof key === 'function' ? key(item) : item[key];
    (acc[k] = acc[k] || []).push(item);
    return acc;
  }, {});

export const sortBy = (arr, key, dir = 'asc') =>
  [...arr].sort((a, b) => {
    const va = typeof key === 'function' ? key(a) : a[key];
    const vb = typeof key === 'function' ? key(b) : b[key];
    return dir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });
